/** Standard report library: generate now, download history and manage email schedules. */
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { useMembers } from "@/features/admin/identity/queries";
import { CustomReportDialog } from "@/features/reports/CustomReportDialog";
import {
  type CustomReport,
  downloadRun,
  type ReportDefinition,
  type ReportParamsIn,
  type ReportRun,
  type ReportSchedule,
  type ReportScheduleIn,
  type ScheduleAction,
  useCustomReports,
  useDeleteCustomReport,
  useReportCatalogue,
  useReportDatasets,
  useReportRuns,
  useReportSchedules,
  useRequestRun,
  useSaveSchedule,
  useScheduleAction,
} from "@/features/reports/queries";
import { DataTable } from "@/shared/components/data-table";
import { SearchInput } from "@/shared/components/filter-bar";
import { FormDialog } from "@/shared/components/form-dialog";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import { SitePicker } from "@/shared/components/pickers";
import { formatDateTime } from "@/shared/lib/format";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select } from "@/shared/ui/form";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/ui/primitives";

/** URL contract for `/reports`; `run` is the deep link sent in report emails. */
export const reportsSearchSchema = z.object({
  tab: z.enum(["library", "history", "schedules"]).optional(),
  run: z.string().optional(),
});

type ReportsSearch = z.infer<typeof reportsSearchSchema>;

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, day) => day + 1);

/** Localised pack name, falling back to the server's label for packs added later. */
function useReportName(): (key: string, fallback?: string) => string {
  const { t } = useTranslation();
  return (key, fallback) => t(`reports.packs.${key}.name`, { defaultValue: fallback ?? key });
}

/** The browser's IANA timezone, used as the default for new schedules. */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** Human-readable size of a stored file. */
export function formatBytes(size: number): string {
  if (size < 1024) {
    return `${String(size)} B`;
  }
  const kb = size / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

/** Shared parameter fields: optional site and look-back/look-ahead window. */
function ParamFields({
  prefix,
  siteId,
  days,
  onSite,
  onDays,
  errors,
}: {
  prefix: string;
  siteId: string;
  days: string;
  onSite: (id: string) => void;
  onDays: (days: string) => void;
  errors: Partial<Record<string, string>>;
}): ReactElement {
  const { t } = useTranslation();
  const { me } = useAuth();
  const scoped = (me?.site_scope?.length ?? 0) > 0;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field
        id={`${prefix}-site`}
        label={t("reports.site")}
        hint={t(scoped ? "reports.siteRequired" : "reports.allSitesHint")}
        error={errors.site_id}
      >
        <SitePicker
          id={`${prefix}-site`}
          value={siteId}
          required={scoped}
          placeholder={t("reports.allSites")}
          onChange={(id) => onSite(id ?? "")}
        />
      </Field>
      <Field
        id={`${prefix}-days`}
        label={t("reports.days")}
        hint={t("reports.daysHint")}
        error={errors.days}
      >
        <Input
          id={`${prefix}-days`}
          type="number"
          min={1}
          max={365}
          inputMode="numeric"
          value={days}
          onChange={(event) => onDays(event.target.value)}
        />
      </Field>
    </div>
  );
}

function toParams(siteId: string, days: string): ReportParamsIn {
  return { site_id: siteId || null, days: days ? Number(days) : null };
}

/** Generate a report now; the file appears in the history tab when ready. */
export function GenerateReportDialog({
  report,
  onClose,
  onQueued,
}: {
  report: ReportDefinition;
  onClose: () => void;
  onQueued: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const reportName = useReportName();
  const request = useRequestRun();
  const form = useForm({
    initial: { site_id: "", days: String(report.default_days) },
    idPrefix: "generate",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((values) =>
      request.mutateAsync({ key: report.key, params: toParams(values.site_id, values.days) }),
    );
    if (ok) {
      onQueued();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("reports.generateTitle", { name: reportName(report.key, report.name) })}
      description={t("reports.generateHint")}
      onSubmit={submit}
      submitLabel={t("reports.generate")}
      submitting={form.submitting}
      error={form.errors._}
    >
      <ParamFields
        prefix="generate"
        siteId={form.values.site_id}
        days={form.values.days}
        onSite={(id) => form.set("site_id", id)}
        onDays={(days) => form.set("days", days)}
        errors={form.errors}
      />
    </FormDialog>
  );
}

/** Checkbox list of active members; without member-directory access only "me" is offered. */
function RecipientPicker({
  value,
  onChange,
  error,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}): ReactElement {
  const { t } = useTranslation();
  const { can, me } = useAuth();
  const [q, setQ] = useState<string>();
  const directory = can("identity.user.view");
  const members = useMembers({ q, status: "active" });
  const toggle = (id: string, checked: boolean): void => {
    onChange(checked ? [...new Set([...value, id])] : value.filter((v) => v !== id));
  };
  return (
    <Field
      id="schedule-recipients"
      label={t("reports.recipients")}
      hint={t("reports.recipientsHint")}
      error={error}
    >
      <div className="space-y-2" id="schedule-recipients">
        {directory ? (
          <SearchInput value={q} onChange={setQ} placeholder={t("reports.searchMembers")} />
        ) : null}
        <div
          className="max-h-52 overflow-y-auto rounded-md border p-2"
          role="group"
          aria-label={t("reports.recipients")}
        >
          {!directory ? (
            me ? (
              <Checkbox
                id="recipient-me"
                label={me.display_name || me.email}
                checked={value.includes(me.membership_id ?? "")}
                onChange={(event) => toggle(me.membership_id ?? "", event.target.checked)}
              />
            ) : null
          ) : members.query.isPending ? (
            <p className="text-sm text-muted-foreground">{t("app.loading")}</p>
          ) : members.query.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage(members.query.error)}
            </p>
          ) : (
            members.items.map((member) => (
              <Checkbox
                key={member.id}
                id={`recipient-${member.id}`}
                className="flex"
                label={member.user.display_name || member.user.email}
                checked={value.includes(member.id)}
                onChange={(event) => toggle(member.id, event.target.checked)}
              />
            ))
          )}
          {directory && members.hasMore ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={members.isLoadingMore}
              onClick={members.loadMore}
            >
              {t("app.loadMore")}
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("reports.recipientsSelected", { count: value.length })}
        </p>
      </div>
    </Field>
  );
}

interface ScheduleForm extends Record<string, unknown> {
  name: string;
  report_key: string;
  site_id: string;
  days: string;
  cadence: ReportScheduleIn["cadence"];
  weekday: string;
  day_of_month: string;
  hour: string;
  timezone: string;
  recipient_ids: string[];
}

function scheduleInitial(
  schedule: ReportSchedule | undefined,
  report: ReportDefinition | undefined,
  me: string | null | undefined,
): ScheduleForm {
  if (schedule) {
    const params = schedule.params as { site_id?: string | null; days?: number };
    return {
      name: schedule.name,
      report_key: schedule.report_key,
      site_id: params.site_id ?? "",
      days: params.days !== undefined ? String(params.days) : "",
      cadence: schedule.cadence as ScheduleForm["cadence"],
      weekday: String(schedule.weekday ?? 0),
      day_of_month: String(schedule.day_of_month ?? 1),
      hour: String(schedule.hour),
      timezone: schedule.timezone,
      recipient_ids: schedule.recipient_ids,
    };
  }
  return {
    name: "",
    report_key: report?.key ?? "",
    site_id: "",
    days: report ? String(report.default_days) : "",
    cadence: "weekly",
    weekday: "0",
    day_of_month: "1",
    hour: "7",
    timezone: browserTimezone(),
    recipient_ids: me ? [me] : [],
  };
}

/** Create or edit a schedule: report, parameters, cadence and recipients. */
export function ScheduleDialog({
  schedule,
  report,
  catalogue,
  onClose,
}: {
  schedule?: ReportSchedule;
  report?: ReportDefinition;
  catalogue: ReportDefinition[];
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { me } = useAuth();
  const reportName = useReportName();
  const save = useSaveSchedule();
  const [initial] = useState(() => scheduleInitial(schedule, report, me?.membership_id));
  const form = useForm<ScheduleForm>({
    initial,
    idPrefix: "schedule",
    schema: z.object({
      name: z.string().trim().min(1, t("app.required")),
      report_key: z.string().min(1, t("app.required")),
      recipient_ids: z.array(z.string()).min(1, t("reports.recipientsRequired")),
    }),
  });
  const v = form.values;
  const submit = async (): Promise<void> => {
    const ok = await form.submit((values) =>
      save.mutateAsync({
        id: schedule?.id,
        body: {
          name: values.name.trim(),
          report_key: values.report_key,
          params: toParams(values.site_id, values.days),
          cadence: values.cadence,
          weekday: values.cadence === "weekly" ? Number(values.weekday) : null,
          day_of_month: values.cadence === "monthly" ? Number(values.day_of_month) : null,
          hour: Number(values.hour),
          timezone: values.timezone.trim() || "UTC",
          recipient_ids: values.recipient_ids,
        },
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(schedule ? "reports.editSchedule" : "reports.newSchedule")}
      description={t("reports.scheduleHint")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      dirty={form.dirty}
      wide
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="schedule-name" label={t("reports.scheduleName")} error={form.errors.name}>
          <Input {...form.field("name")} maxLength={150} required />
        </Field>
        <Field id="schedule-report_key" label={t("reports.report")} error={form.errors.report_key}>
          <Select
            {...form.field("report_key")}
            onChange={(event) => {
              const next = catalogue.find((r) => r.key === event.target.value);
              form.patch({
                report_key: event.target.value,
                days: next ? String(next.default_days) : v.days,
              });
            }}
            required
          >
            <option value="">{t("reports.chooseReport")}</option>
            {catalogue.map((r) => (
              <option key={r.key} value={r.key}>
                {reportName(r.key, r.name)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <ParamFields
        prefix="schedule"
        siteId={v.site_id}
        days={v.days}
        onSite={(id) => form.set("site_id", id)}
        onDays={(days) => form.set("days", days)}
        errors={form.errors}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field id="schedule-cadence" label={t("reports.cadence")} error={form.errors.cadence}>
          <Select
            {...form.field("cadence")}
            onChange={(event) => form.set("cadence", event.target.value as ScheduleForm["cadence"])}
          >
            {(["daily", "weekly", "monthly"] as const).map((c) => (
              <option key={c} value={c}>
                {t(`reports.cadences.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
        {v.cadence === "weekly" ? (
          <Field id="schedule-weekday" label={t("reports.weekday")} error={form.errors.weekday}>
            <Select {...form.field("weekday")}>
              {WEEKDAYS.map((d) => (
                <option key={d} value={String(d)}>
                  {t(`reports.weekdays.${String(d)}`)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {v.cadence === "monthly" ? (
          <Field
            id="schedule-day_of_month"
            label={t("reports.dayOfMonth")}
            error={form.errors.day_of_month}
          >
            <Select {...form.field("day_of_month")}>
              {DAYS_OF_MONTH.map((d) => (
                <option key={d} value={String(d)}>
                  {d}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field id="schedule-hour" label={t("reports.hour")} error={form.errors.hour}>
          <Select {...form.field("hour")}>
            {HOURS.map((h) => (
              <option key={h} value={String(h)}>
                {`${String(h).padStart(2, "0")}:00`}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="schedule-timezone" label={t("reports.timezone")} error={form.errors.timezone}>
          <Input {...form.field("timezone")} dir="ltr" autoComplete="off" />
        </Field>
      </div>
      <RecipientPicker
        value={v.recipient_ids}
        onChange={(ids) => form.set("recipient_ids", ids)}
        error={form.errors.recipient_ids}
      />
    </FormDialog>
  );
}

/** Library, history and schedules tabs for the standard and custom report packs. */
export function ReportsPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const { can, me } = useAuth();
  const navigate = useNavigate();
  const search: ReportsSearch = useSearch({ strict: false });
  const update = (patch: Partial<ReportsSearch>): void => {
    void navigate({ to: ".", search: (previous) => ({ ...previous, ...patch }) });
  };
  const tab = search.tab ?? (search.run ? "history" : "library");
  const reportName = useReportName();
  const catalogue = useReportCatalogue();
  const customReports = useCustomReports();
  const datasets = useReportDatasets();
  const deleteCustom = useDeleteCustomReport();
  const runs = useReportRuns();
  const canSchedule = can("reporting.report.schedule");
  const canExport = can("reporting.report.export");
  const schedules = useReportSchedules();
  const action = useScheduleAction();
  const [generating, setGenerating] = useState<ReportDefinition | null>(null);
  const [editing, setEditing] = useState<{
    schedule?: ReportSchedule;
    report?: ReportDefinition;
  } | null>(null);
  const [editingCustom, setEditingCustom] = useState<{ report?: CustomReport } | null>(null);
  const [confirmDeleteCustom, setConfirmDeleteCustom] = useState<CustomReport | null>(null);
  const [downloadError, setDownloadError] = useState<unknown>();
  const [confirmDelete, setConfirmDelete] = useState<ReportSchedule | null>(null);
  const packs = catalogue.data ?? [];
  const customList = customReports.data ?? [];
  const customTitle = (cr: CustomReport): string => cr.name_i18n[i18n.language] ?? cr.name;
  const customDesc = (cr: CustomReport): string =>
    cr.description_i18n[i18n.language] ?? cr.description;
  const customAsDef = (cr: CustomReport): ReportDefinition => ({
    key: cr.report_key,
    name: customTitle(cr),
    description: customDesc(cr),
    default_days: cr.default_days,
    can_schedule: cr.can_schedule,
  });
  const allSchedulable: ReportDefinition[] = [...packs, ...customList.map(customAsDef)];
  const nameOf = (key: string): string => {
    const customMatch = customList.find(
      (c) => c.report_key === key || (c.slug && `custom:${c.slug}` === key),
    );
    if (customMatch) return customTitle(customMatch);
    return reportName(key, packs.find((p) => p.key === key)?.name);
  };
  const manages = (schedule: ReportSchedule): boolean =>
    can("reporting.report.manage_all") || (canSchedule && schedule.owner_id === me?.membership_id);
  const runAction = (id: string, kind: ScheduleAction): void => {
    action.mutate({ id, action: kind });
  };
  const cadenceText = (s: ReportSchedule): string => {
    const time = `${String(s.hour).padStart(2, "0")}:00 ${s.timezone}`;
    if (s.cadence === "weekly") {
      return t("reports.cadenceWeekly", {
        day: t(`reports.weekdays.${String(s.weekday ?? 0)}`),
        time,
      });
    }
    if (s.cadence === "monthly") {
      return t("reports.cadenceMonthly", { day: s.day_of_month ?? 1, time });
    }
    return t("reports.cadenceDaily", { time });
  };

  const runColumns: ColumnDef<ReportRun>[] = [
    {
      id: "report",
      header: t("reports.report"),
      cell: ({ row }) => (
        <div className="min-w-0">
          <span className={row.original.id === search.run ? "font-semibold" : undefined}>
            {nameOf(row.original.report_key)}
          </span>
          <div className="text-xs text-muted-foreground">
            {row.original.schedule_id ? t("reports.scheduled") : t("reports.onDemand")}
          </div>
        </div>
      ),
    },
    {
      id: "status",
      header: t("app.status"),
      cell: ({ row }) => (
        <div>
          <StatusBadge value={row.original.status} namespace="reports.status" />
          {row.original.error ? (
            <div className="text-xs text-destructive [overflow-wrap:anywhere]">
              {t(`reports.errors.${row.original.error.split(":")[0] ?? "error"}`, {
                defaultValue: row.original.error,
              })}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      id: "requested",
      header: t("reports.requestedAt"),
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    {
      id: "expires",
      header: t("reports.expiresAt"),
      cell: ({ row }) => formatDateTime(row.original.expires_at),
    },
    {
      id: "size",
      header: t("reports.size"),
      cell: ({ row }) =>
        row.original.status === "succeeded"
          ? t("reports.sizeRows", {
              size: formatBytes(row.original.size),
              count: row.original.row_count,
            })
          : "—",
    },
    {
      id: "actions",
      header: t("app.actions"),
      cell: ({ row }) =>
        row.original.status === "succeeded" ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDownloadError(undefined);
              void downloadRun(row.original).catch(setDownloadError);
            }}
          >
            {t("reports.download")}
          </Button>
        ) : null,
    },
  ];

  const scheduleColumns: ColumnDef<ReportSchedule>[] = [
    {
      id: "name",
      header: t("reports.scheduleName"),
      cell: ({ row }) => (
        <div className="min-w-0 [overflow-wrap:anywhere]">
          {row.original.name}
          <div className="text-xs text-muted-foreground">{nameOf(row.original.report_key)}</div>
        </div>
      ),
    },
    {
      id: "cadence",
      header: t("reports.cadence"),
      cell: ({ row }) => cadenceText(row.original),
    },
    {
      id: "next",
      header: t("reports.nextRun"),
      cell: ({ row }) =>
        row.original.is_active ? (
          formatDateTime(row.original.next_run_at)
        ) : (
          <div>
            <StatusBadge value="paused" namespace="reports.status" />
            {row.original.paused_reason ? (
              <div className="text-xs text-muted-foreground">
                {t(`reports.errors.${row.original.paused_reason.split(":")[0] ?? "error"}`, {
                  defaultValue: row.original.paused_reason,
                })}
              </div>
            ) : null}
          </div>
        ),
    },
    {
      id: "recipients",
      header: t("reports.recipients"),
      cell: ({ row }) => row.original.recipient_ids.length,
    },
    {
      id: "actions",
      header: t("app.actions"),
      cell: ({ row }) =>
        manages(row.original) ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing({ schedule: row.original })}
            >
              {t("app.edit")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={action.isPending}
              onClick={() =>
                runAction(row.original.id, row.original.is_active ? "pause" : "resume")
              }
            >
              {t(row.original.is_active ? "reports.pause" : "reports.resume")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={action.isPending}
              onClick={() => runAction(row.original.id, "run")}
            >
              {t("reports.runNow")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmDelete(row.original)}>
              {t("app.delete")}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t("reports.title")}
        subtitle={t("reports.subtitle")}
        actions={
          <div className="flex flex-wrap gap-2">
            {canExport && (datasets.data?.length ?? 0) > 0 ? (
              <Button variant="outline" onClick={() => setEditingCustom({})}>
                {t("reports.newCustomReport")}
              </Button>
            ) : null}
            {canSchedule && allSchedulable.length ? (
              <Button onClick={() => setEditing({})}>{t("reports.newSchedule")}</Button>
            ) : null}
          </div>
        }
      />
      <Tabs value={tab} onValueChange={(value) => update({ tab: value as ReportsSearch["tab"] })}>
        <TabsList className="mb-4 flex w-full flex-wrap justify-start">
          <TabsTrigger value="library">{t("reports.library")}</TabsTrigger>
          <TabsTrigger value="history">{t("reports.history")}</TabsTrigger>
          <TabsTrigger value="schedules">{t("reports.schedules")}</TabsTrigger>
        </TabsList>
        <TabsContent value="library">
          {catalogue.isPending ? (
            <LoadingState />
          ) : catalogue.error ? (
            <ErrorState error={catalogue.error} />
          ) : packs.length || customList.length ? (
            <div className="space-y-6">
              {packs.length ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    {t("reports.standardPacks")}
                  </h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {packs.map((report) => (
                      <Card key={report.key} className="min-w-0">
                        <CardHeader>
                          <CardTitle>{reportName(report.key, report.name)}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            {t(`reports.packs.${report.key}.description`, {
                              defaultValue: report.description,
                            })}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button onClick={() => setGenerating(report)}>
                              {t("reports.generate")}
                            </Button>
                            {report.can_schedule ? (
                              <Button variant="outline" onClick={() => setEditing({ report })}>
                                {t("reports.schedule")}
                              </Button>
                            ) : null}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : null}

              {customList.length ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    {t("reports.customAndRolePacks")}
                  </h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {customList.map((cr) => {
                      const def = customAsDef(cr);
                      return (
                        <Card key={cr.id} className="min-w-0">
                          <CardHeader className="flex flex-row items-start justify-between gap-2">
                            <CardTitle className="text-base">{customTitle(cr)}</CardTitle>
                            <div className="flex shrink-0 flex-wrap gap-1">
                              {cr.target_role ? (
                                <Badge tone="info">
                                  {t(`reports.roles.${cr.target_role}`, {
                                    defaultValue: cr.target_role,
                                  })}
                                </Badge>
                              ) : null}
                              <Badge tone="outline">
                                {t("reports.sheetsCount", { count: cr.sections.length })}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <p className="text-sm text-muted-foreground">{customDesc(cr)}</p>
                            <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
                              {cr.sections.map((s, idx) => (
                                <span
                                  key={idx}
                                  className="rounded border bg-muted/40 px-1.5 py-0.5"
                                >
                                  {s.title || s.dataset}
                                </span>
                              ))}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button onClick={() => setGenerating(def)}>
                                {t("reports.generate")}
                              </Button>
                              {cr.can_schedule ? (
                                <Button
                                  variant="outline"
                                  onClick={() => setEditing({ report: def })}
                                >
                                  {t("reports.schedule")}
                                </Button>
                              ) : null}
                              {cr.can_manage ? (
                                <>
                                  <Button
                                    variant="outline"
                                    onClick={() => setEditingCustom({ report: cr })}
                                  >
                                    {t("app.edit")}
                                  </Button>
                                  {!cr.is_system ? (
                                    <Button
                                      variant="outline"
                                      onClick={() => setConfirmDeleteCustom(cr)}
                                    >
                                      {t("app.delete")}
                                    </Button>
                                  ) : null}
                                </>
                              ) : null}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <EmptyState message={t("reports.noReports")} />
          )}
        </TabsContent>
        <TabsContent value="history">
          {downloadError ? (
            <p role="alert" className="mb-3 text-sm text-destructive">
              {errorMessage(downloadError)}
            </p>
          ) : null}
          {runs.isPending ? (
            <LoadingState />
          ) : runs.error ? (
            <ErrorState error={runs.error} />
          ) : (
            <DataTable
              columns={runColumns}
              data={runs.data}
              getRowId={(run) => run.id}
              emptyMessage={t("reports.noRuns")}
            />
          )}
        </TabsContent>
        <TabsContent value="schedules">
          {action.error ? (
            <p role="alert" className="mb-3 text-sm text-destructive">
              {errorMessage(action.error)}
            </p>
          ) : null}
          {schedules.isPending ? (
            <LoadingState />
          ) : schedules.error ? (
            <ErrorState error={schedules.error} />
          ) : (
            <DataTable
              columns={scheduleColumns}
              data={schedules.data}
              getRowId={(s) => s.id}
              emptyMessage={t("reports.noSchedules")}
            />
          )}
        </TabsContent>
      </Tabs>
      {generating ? (
        <GenerateReportDialog
          report={generating}
          onClose={() => setGenerating(null)}
          onQueued={() => {
            setGenerating(null);
            update({ tab: "history" });
          }}
        />
      ) : null}
      {editing ? (
        <ScheduleDialog
          schedule={editing.schedule}
          report={editing.report}
          catalogue={allSchedulable}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {editingCustom && datasets.data ? (
        <CustomReportDialog
          report={editingCustom.report}
          datasets={datasets.data}
          onClose={() => setEditingCustom(null)}
        />
      ) : null}
      {confirmDeleteCustom ? (
        <FormDialog
          open
          onOpenChange={(open) => !open && setConfirmDeleteCustom(null)}
          title={t("reports.deleteCustomTitle")}
          description={t("reports.deleteHint", { name: customTitle(confirmDeleteCustom) })}
          submitLabel={t("app.delete")}
          onSubmit={async () => {
            await deleteCustom.mutateAsync(confirmDeleteCustom.id);
            setConfirmDeleteCustom(null);
          }}
          submitting={deleteCustom.isPending}
        >
          <span />
        </FormDialog>
      ) : null}
      {confirmDelete ? (
        <FormDialog
          open
          onOpenChange={(open) => !open && setConfirmDelete(null)}
          title={t("reports.deleteTitle")}
          description={t("reports.deleteHint", { name: confirmDelete.name })}
          submitLabel={t("app.delete")}
          onSubmit={async () => {
            await action.mutateAsync({ id: confirmDelete.id, action: "delete" });
            setConfirmDelete(null);
          }}
          submitting={action.isPending}
        >
          <span />
        </FormDialog>
      ) : null}
    </>
  );
}
